import React, { ReactNode } from "react";
import { Helmet } from "react-helmet";
import {
  editBusinessMatchingOwnedBusinessEntityPath,
  newBusinessMatchingOwnedBusinessEntityPath,
  rootPath,
} from "../../../../../routes";
import Button from "../../../shared/components/ui/Button";
import { Box } from "../../../shared/components/ui/chakra";
import MessagesButton from "../../../shared/components/app/MessagesButton";
import RandomColorAvatar from "../../../shared/components/app/RandomColorAvatar";
import ReadonlyUserAlert from "../../../shared/components/app/ReadonlyUserAlert";
import AddIcon from "../../../shared/components/icons/AddIcon";
import ArrowRightAltIcon from "../../../shared/components/icons/ArrowRightAltIcon";
import SettingsIcon from "../../../shared/components/icons/SettingsIcon";
import {
  SharedCurrentUser,
  SharedOwnedBusinessEntities,
  SharedOwnedBusinessEntity,
} from "../../../shared/lib/types";
import { LogoSVG } from "../../../svg";
import { useSetCurrentBusinessEntityCode } from "../lib/useSetCurrentBusinessEntityCode";
import BusinessEntityStatusTag from "./BusinessEntityStatusTag";
import BusinessMatchingHeaderDrawer from "./BusinessMatchingHeaderDrawer";
import { addBusinessEntityButtonLabel } from "../lib/addBusinessEntityButtonLabel";
import { filterNotCurrentOwnedBusinessEntities } from "../lib/filterNotCurrentOwnedBusinessEntities";


import {
  Flex,
  Grid,
  GridItem,
  HStack,
  Image,
  Link,
  Menu,
  MenuButton,
  MenuDivider,
  MenuGroup,
  MenuItem,
  MenuList,
  Show,
  Stack,
  Text,
} from "@chakra-ui/react";

const hoverStyle = { backgroundColor: "#EDF5F3" };

const BusinessMatchingHeader = ({
  currentUser,
  disableBackLink = false,
  disableMessagesButton = false,
  disableMenu = false,
}: {
  currentUser: SharedCurrentUser;
  disableBackLink?: boolean;
  disableMessagesButton?: boolean;
  disableMenu?: boolean;
}) => {
  return (
    <>
      <Helmet>
        <style type="text/css">{`
          html {
            scroll-padding-top: 120px;
          }
        `}</style>
      </Helmet>
      <Box position="sticky" top="0px" zIndex="100">
        {currentUser?.is_readonly_user && (
          <ReadonlyUserAlert />
        )}
        <Grid
          bg="white"
          boxShadow={"0px 8px 16px -8px rgba(10, 100, 79, 0.12)"}
          zIndex={2}
          pl={{ base: "16px", md: 6 }}
          h={{ base: "62px", md: 14 }}
          position="sticky"
          top={0}
          templateColumns={{ base: "1fr 1fr", xl: "1fr auto 1fr" }}
          gap={6}
        >
          <Show above="xl">
            <GridItem gap={2} display={"flex"} alignItems={"center"}>
              {!disableBackLink && (
                <>
                  <ArrowRightAltIcon boxSize={6} color={"textLink"} />
                  <Link
                    pt={0.5}
                    textDecoration={"none"}
                    _hover={{ textDecoration: "underline" }}
                    href={rootPath()}
                  >
                    Q&A・勉強会に戻る
                  </Link>
                </>
              )}
            </GridItem>
          </Show>
          <GridItem gap={2} display={"flex"} alignItems={"center"}>
            <Box pb={1}>
              <Image src={LogoSVG} h={7} />
            </Box>
            <Text fontWeight={"bold"} color={"brand.500"} pt={0.5}>
              受発注β版（無料）
            </Text>
          </GridItem>
          <GridItem display={"flex"} justifyContent={"flex-end"}>
            <HStack h="full" gap={0}>
              {currentUser?.has_any_business_entities && (
                <>
                  {!disableMessagesButton && (
                    <MessagesButton currentUser={currentUser} />
                  )}
                  <Show above="xl">
                    <Menu autoSelect={false}>
                      <MenuButton
                        h="full"
                        _hover={
                          disableMenu ? { cursor: "default" } : hoverStyle
                        }
                        px={4}
                      >
                        <BusinessEntityProfile
                          businessEntity={
                            currentUser.owned_business_entities.current
                          }
                        />
                      </MenuButton>
                      {!disableMenu && (
                        <PopUpMenu
                          ownedBusinessEntities={
                            currentUser.owned_business_entities
                          }
                          currentUser={currentUser}
                        />
                      )}
                    </Menu>
                  </Show>
                </>
              )}
              <Show below="xl">
                <Flex h="full" alignItems="center" mr={4}>
                  <BusinessMatchingHeaderDrawer currentUser={currentUser} />
                </Flex>
              </Show>
            </HStack>
          </GridItem>
        </Grid>
      </Box>
    </>
  );
};

const BusinessEntityProfile = ({
  businessEntity,
}: {
  businessEntity: SharedOwnedBusinessEntity;
}) => {
  return (
    <HStack gap={2}>
      <RandomColorAvatar
        src={businessEntity.logo_image_url}
        bgColor={businessEntity.business_entity_avatar_bgcolor}
        name={businessEntity.name}
        boxSize={7}
        sx={{ "--avatar-font-size": "0.75rem" }}
      />
      <Text fontWeight="bold" pt={0.5} whiteSpace="nowrap" overflow="hidden" textOverflow="ellipsis">
        {businessEntity.name}
      </Text>
      <BusinessEntityStatusTag businessEntity={businessEntity} />
    </HStack>
  );
};

const CustomMenuItem = ({
  href = undefined,
  target = undefined,
  onClick = undefined,
  rel = undefined,
  children,
}: {
  href?: string;
  target?: string;
  rel?: string;
  onClick?: () => void;
  children: ReactNode;
}) => (
  <MenuItem
    fontSize="sm"
    px={5}
    as={Link}
    href={href}
    onClick={onClick}
    target={target}
    rel={rel}
    _hover={hoverStyle}
    flexDirection={"column"}
    alignItems={"flex-start"}
    pb={0}
    pt={4}
  >
    {children}
  </MenuItem>
);

const PopUpMenu = ({
  currentUser,
  ownedBusinessEntities,
}: {
  currentUser: Pick<
    NonNullable<SharedCurrentUser>,
    "has_draft_first_business_entity_application"
  >;
  ownedBusinessEntities: NonNullable<SharedOwnedBusinessEntities>;
}) => {
  const setCurrentBusinessEntityCode = useSetCurrentBusinessEntityCode();
  const withoutCurrentOwnedBusinessEntities =
    filterNotCurrentOwnedBusinessEntities(ownedBusinessEntities);
  const hasMultipleBusinessEntities =
    withoutCurrentOwnedBusinessEntities.length > 0;

  return (
    <MenuList pt={0} pb={0} minW="360px">
      <MenuGroup>
        <Stack p={5} gap={3}>
          <BusinessEntityProfile
            businessEntity={ownedBusinessEntities.current}
          />
          <Button
            size={"sm"}
            variant="outline"
            leftIcon={<SettingsIcon boxSize={"18px"} />}
            as="a"
            target="_blank"
            rel="noreferrer"
            href={editBusinessMatchingOwnedBusinessEntityPath(
              ownedBusinessEntities.current.code,
            )}
          >
            事業者設定
          </Button>
        </Stack>
      </MenuGroup>
      <MenuDivider m={0} />
      <MenuGroup
        title={hasMultipleBusinessEntities ? "そのほか利用中の事業者一覧" : ""}
        px={5}
        pt={5}
        pb={2}
        m={0}
      >
        {withoutCurrentOwnedBusinessEntities.map((businessEntity) => (
          <CustomMenuItem
            key={businessEntity.code}
            onClick={() => setCurrentBusinessEntityCode(businessEntity.code)}
          >
            <Stack gap={1}>
              <Text fontSize={"sm"}>{businessEntity.name}</Text>
              <BusinessEntityStatusTag businessEntity={businessEntity} />
            </Stack>
            <MenuDivider w={"full"} mt={4} mb={0} />
          </CustomMenuItem>
        ))}
        <Stack p={5}>
          <Button
            size={"xs"}
            variant="outline"
            leftIcon={<AddIcon boxSize={4} />}
            as="a"
            href={newBusinessMatchingOwnedBusinessEntityPath()}
          >
            {addBusinessEntityButtonLabel(currentUser)}
          </Button>
        </Stack>
      </MenuGroup>
    </MenuList>
  );
};
export default BusinessMatchingHeader;
